import { HttpErrorResponse } from '@angular/common/http';
import type { ErrorHandler } from '@angular/core';
import { Injectable, Injector, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Actions } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import * as Sentry from '@sentry/browser';
import type { StartSpanOptions } from '@sentry/core';
import { config } from 'rxjs';

import { getPathFromRoute } from '../helpers';
import { AccountBase, SentryConfig } from './config';
import { RtLocation } from './location.service';

const injectActions = () => {
  try {
    return inject(Actions, { optional: true });
  } catch {
    return null;
  }
};

enum TransactionOp {
  PAGE_LOAD = 'pageload',
  NAVIGATION = 'navigation',
}

@Injectable()
export class RtcErrorHandler implements ErrorHandler {
  private readonly sentryConfig = inject(SentryConfig);
  private readonly injector = inject(Injector);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(RtLocation);

  private readonly NGRX_ACTIONS_CONTEXT_SIZE = 25;
  private readonly filteredStatuses: number[] = [0, 401, 403, 404];
  private readonly appRevision: string = this.injector.get(this.sentryConfig.revisionToken);
  private readonly ignoredEvents: Array<string | RegExp> = [
    'Http request failed',
    'Socket close code 1000',
    '[Analytics] Segment client failed to load',
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    'channel failed because of timeout',
  ];
  private nextPath: string;
  private actions$ = injectActions();
  private ngrxActions: Action[] = [];

  // eslint-disable-next-line max-lines-per-function
  constructor() {
    const sentryConfig = this.sentryConfig;

    if (!this.sentryConfig.enable) {
      return;
    }

    this.location.go$.subscribe(path => (this.nextPath = path));

    config.onUnhandledError = (err: any) => this.rxjsOnUnhandledError(err);

    Sentry.init({
      dsn: this.sentryConfig.dsn,
      release: this.appRevision,
      integrations: [
        Sentry.rewriteFramesIntegration({
          iteratee: frame => {
            if (frame.filename) {
              frame.in_app = /(.*)(runtime|main|common|\d+)\.(.*)\.js/.test(frame.filename);
            }
            return frame;
          },
        }),
        Sentry.browserTracingIntegration({
          enableLongAnimationFrame: false,
          ignoreResourceSpans: [
            'resource.script',
            'resource.css',
            'resource.link',
            'resource.other',
            'resource.img',
            'resource.icon',
            'resource.iframe',
          ],
          beforeStartSpan: (context: StartSpanOptions): StartSpanOptions => ({
            ...context,
            ...this.getTransactionName(context),
          }),
        }),
      ],
      ignoreSpans: [{ op: /^browser\./ }, { op: 'mark' }, { op: 'measure' }],
      tracesSampler: samplingContext => {
        if (TransactionOp.PAGE_LOAD === samplingContext.name) {
          return 0.02;
        }
        if (samplingContext.attributes?.['sentry.origin']?.includes(TransactionOp.NAVIGATION)) {
          return 0.001;
        }

        return 0.1;
      },
      beforeBreadcrumb: (breadcrumb, hint) =>
        // This will filter out clicks/typing
        breadcrumb.category && breadcrumb.category.includes('ui.') ? null : breadcrumb,
      beforeSend: (event, hint) => {
        if (!this.sentryConfig.enable) return null;
        // @ts-ignore
        const httpStatus = event.extra?.__serialized__?.status;
        const isStatusValid =
          httpStatus === undefined || !this.filteredStatuses.includes(httpStatus);

        return isStatusValid ? event : null;
      },
      maxBreadcrumbs: 30,
      ignoreErrors: this.ignoredEvents,
      environment: sentryConfig.environmentName,
    });

    if (this.actions$) {
      this.actions$.subscribe(
        ({ type }) =>
          (this.ngrxActions = [{ type }, ...this.ngrxActions].slice(
            0,
            this.NGRX_ACTIONS_CONTEXT_SIZE,
          )),
      );
    }

    /**
     * NgRx store devtools requires ErrorService
     * Here we need currentAdmin but it is empty because AccountBase is not initialized yet
     * setTimeout fixes this issue
     */
    setTimeout(() => {
      const account = this.injector.get(AccountBase);
      account.currentAdmin$.subscribe(currentAdmin =>
        Sentry.setUser({
          id: `${currentAdmin.id}`,
          email: currentAdmin.email,
          username: `${currentAdmin.firstName} ${currentAdmin.lastName}`,
          roleName: currentAdmin.roleName,
        }),
      );

      account.currentCompany$.subscribe(currentCompany =>
        Sentry.setExtra('Company', currentCompany?.id),
      );
    });
  }

  public handleError(err: Error, showInConsole: boolean = !this.sentryConfig.enable): void {
    try {
      if (this.sentryConfig.enable) {
        if (err instanceof HttpErrorResponse) {
          if (!this.filteredStatuses.includes(err.status)) {
            Sentry.captureMessage(this.getHttpErrorResponseMessage(err));
          }
        } else {
          this.sendErrorToSentry(err);
        }
      }
    } finally {
      if (showInConsole) {
        console.error(err);
      }
    }
  }

  public trace(name: string, showInConsole: boolean): void {
    try {
      throw new Error(name);
    } catch (e) {
      this.handleError(e, showInConsole);
    }
  }

  private rxjsOnUnhandledError(err: any) {
    if (err instanceof Error) return this.handleError(err);

    try {
      const message = this.getErrorTypeMessage(err);
      const isIgnored = this.ignoredEvents.some(stringOrRegexp =>
        typeof stringOrRegexp === 'string'
          ? message.includes(stringOrRegexp)
          : stringOrRegexp.test(message),
      );
      if (isIgnored) return;

      throw new Error(`Unhandled rxjs error ${message}`);
    } catch (e) {
      this.handleError(e);
    }
  }

  private getErrorTypeMessage(err: any) {
    if (err instanceof HttpErrorResponse) {
      return this.getHttpErrorResponseMessage(err);
    } else if (typeof err === 'object' && err !== null) {
      return `keys: ${Object.keys(err)}`;
    } else {
      return `value: ${err}`;
    }
  }

  private getHttpErrorResponseMessage(response: HttpErrorResponse): string {
    return `Http request failed. (${response.url}, ${response.status}, ${response.message})`;
  }

  private sendErrorToSentry(err: any): void {
    const error = err.originalError || err;
    const message: string = error?.message ?? '';

    // Chunk load errors get their own handler with richer context (HTTP status, connection info, etc.)
    // rather than being reported as fatal errors alongside real application crashes
    const chunkUrlMatch = message.match(/(https?:\/\/\S+\.js)/);
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      chunkUrlMatch !== null ||
      message.includes('Importing a module script failed') || // Safari
      message.includes('error loading dynamically imported module') || // Firefox
      message.includes('Failed to fetch dynamically imported module'); // Chrome

    // Route chunk errors to dedicated handler — captured as warnings, not fatal crashes
    if (isChunkError) {
      this.captureChunkLoadError(error, chunkUrlMatch?.[1]);
      return;
    }

    // All other errors are treated as fatal application crashes
    Sentry.setTags({ fatal: 'true' });
    Sentry.captureException(error, {
      contexts: {
        ngrxActions: {
          actions: JSON.stringify(this.ngrxActions, null, 2),
        },
      },
    });
  }

  // Captures chunk load failures as warnings with diagnostic context to help understand
  // whether the cause is a stale deploy (404), network issue, or CDN misconfiguration
  private captureChunkLoadError(error: Error, chunkUrl?: string): void {
    try {
      const connection = (<any>navigator).connection;
      const cause = (<any>error).cause;
      // grab the browser's own record of the original failed request before making our HEAD check
      const resourceEntry = chunkUrl
        ? <PerformanceResourceTiming | undefined>performance.getEntriesByName(chunkUrl).at(-1)
        : undefined;

      let ngrxActionsJson: string | undefined;
      try {
        ngrxActionsJson = JSON.stringify(this.ngrxActions, null, 2);
      } catch {
        ngrxActionsJson = undefined;
      }

      const doCapture = (extra: Record<string, unknown> = {}) => {
        const context = {
          chunkUrl: chunkUrl ?? 'unknown',
          currentUrl: window.location.href,
          navigatorOnLine: navigator.onLine,
          connectionType: connection?.effectiveType ?? 'unknown',
          tabVisibility: document.visibilityState,
          // large value here means the user had an old deploy open when the error happened
          secondsSincePageLoad: Math.round(performance.now() / 1000),
          errorCause: cause instanceof Error ? cause.message : (cause ?? 'none'),
          serviceWorker: navigator.serviceWorker?.controller?.scriptURL ?? 'none',
          resourceResponseStatus: resourceEntry?.responseStatus,
          resourceDurationMs: resourceEntry ? Math.round(resourceEntry.duration) : undefined,
          resourceTransferSize: resourceEntry?.transferSize,
          ...extra,
        };
        Sentry.withScope(scope => {
          scope.setTag('chunk_load_error', 'true');
          scope.setContext('Chunk Load', context);
          if (ngrxActionsJson) {
            scope.setContext('ngrxActions', { actions: ngrxActionsJson });
          }
          scope.setLevel('warning');
          Sentry.captureException(error);
        });
      };

      if (chunkUrl) {
        // HEAD the chunk after the fact to find out if it still exists and with what content-type
        fetch(chunkUrl, { method: 'HEAD', cache: 'no-store' })
          .then(response =>
            doCapture({
              chunkHttpStatus: response.status,
              chunkContentType: response.headers.get('content-type') ?? 'unknown',
            }),
          )
          .catch(() => doCapture({ chunkFetchError: 'fetch failed' }));
      } else {
        // Safari doesn't include the URL in the error message so we capture without it
        doCapture();
      }
    } catch {
      // if anything above throws, still report the original error without context
      Sentry.captureException(error);
    }
  }

  private getTransactionName(context: StartSpanOptions): StartSpanOptions {
    if (context.op === TransactionOp.PAGE_LOAD) {
      return { name: context.op };
    }

    const { queryParams: nextQueryParams } = this.router.parseUrl(this.nextPath);
    if (nextQueryParams.candidate) {
      return {
        name: [
          '/candidate_dialog',
          nextQueryParams.tab || 'overview',
          nextQueryParams.openInterview === 'true' && 'open_interview',
        ]
          .filter(Boolean)
          .join('/'),
      };
    }

    return { name: '/' + getPathFromRoute(this.route) };
  }
}
